import {
  createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import useMounted from 'src/common/hooks/useMounted';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { TripCartPanels, TripCartFormState } from 'src/listing/types';
import { getInitialValues } from 'src/trip-cart/utils/tripCartInitialValues';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import {
  MIN_SNAP_POINT, TRIP_CART_PANEL_CALENDAR, TRIP_CART_PANEL_ROOT,
} from 'src/listing/constants';
import { clearCaches } from 'src/trip-cart/utils/tripCartCache';
import { useRouter } from 'next/router';
import type { ListingPageProps } from 'src/common/types/common/ListingPageProps';
import { ListingAddon } from 'src/common/types/common/Addons';
import captureException from 'src/common/utils/captureException';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { removeSearchParams } from 'src/common/hooks/useRemoveSearchParam';
import ensureString from 'src/common/utils/ensureString';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { SessionData } from 'src/checkout/hooks/useProceedToCheckoutStep';
import { InquiryData } from 'src/inquiry/types';
import { decompress } from 'lz-string';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import { PARAM_CHECKOUT_INSTABOOK_GUEST_ERROR } from 'src/checkout/constants';

type TripCartStatus = 'initializing' | 'ready';
type PanelType = 'aside' | 'drawer';

type TripCartContextProps = {
  isOpen: boolean;
  setOpen: (value: boolean, startingPanel?: TripCartPanels) => void;
  listing: FullBoatSearchDetail;
  status: TripCartStatus;
  initialValues: TripCartFormState;
  showPanel: (value: TripCartPanels, autoOpen?: boolean) => void;
  closePanel: () => void;
  activePanel: TripCartPanels;
  /** @deprecated conversion-mission */
  isLargeScreen: boolean;
  /**
   * Which is visible: mobile drawer or desktop aside
   * This is mediaQuery based, use CSS for visibility where possible in rendering
   */
  panelType: PanelType;
  threadId?: string;
  /** @deprecated conversion-mission */
  snap: string;
  /** @deprecated conversion-mission */
  setSnap: (value: string) => void;
  snoozeActive?: boolean;
  addonsOptional?: ListingAddon[];
  addonsLater?: ListingAddon[];
  panelDirection: 'in' | 'out';
  hasDepartureTimes: boolean;
  setHasDepartureTimes: (hasDepartureTimes: boolean) => void;
  /**
   * Value that determines if the AI summary is shown or not on the listing page.
   */
  showAiSummary: boolean;
};

const TripCartContext = createContext<TripCartContextProps>({} as TripCartContextProps);

type TripCartProviderProps = {
  listing: FullBoatSearchDetail;
  tripCartCookieData?: Partial<RecentInquiryData>;
  snoozeActive?: boolean;
  /**
   * If set, the Inquiry Sent banner will be shown
   */
  threadId?: string;
  children: ReactNode;
  showAiSummary: boolean;
} & Pick<ListingPageProps, 'addonsOptional' | 'addonsLater'>;

const TripCartProvider: FC<TripCartProviderProps> = ({
  listing,
  tripCartCookieData,
  snoozeActive,
  threadId,
  children,
  addonsOptional = [],
  addonsLater = [],
  showAiSummary,
}) => {
  const router = useRouter();
  const [status, setStatus] = useState<TripCartStatus>('initializing');
  const { setMotivatorsDates } = useListingMotivators();
  const [isOpen, setOpenInternal] = useState(false);
  const [activePanel, setActivePanel] = useState<TripCartPanels>(TRIP_CART_PANEL_ROOT);
  const [previousPanels, setPreviousPanels] = useState<TripCartPanels[]>([]);
  const [panelDirection, setPanelDirection] = useState<'in' | 'out'>('in');
  const isMounted = useMounted();
  const [hasDepartureTimes, setHasDepartureTimes] = useState(true);

  // Initialization ============================================================
  const { bookingMotivators } = useBookingMotivatorsContext();

  const [initialValues, setInitialValues] = useState((): TripCartFormState => getInitialValues({
    listing,
    bookingMotivators,
    tripCartCookieData,
  }));

  // End initialization =========================================================

  const restoreMessage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const sessionData = sessionStore.getItem(ensureString(router.query.sessionKey));
      if (!sessionData) {
        throw new Error('No session data');
      }

      const parsedData: SessionData | InquiryData = JSON.parse(sessionData);
      if (!parsedData) {
        throw new Error('Unable to parse session or inquiry');
      }

      // set departure time and message from session data for book now.
      if (Object.hasOwn(parsedData, 'availabilityId')) {
        const bookNowData = parsedData as SessionData;
        const { formData } = bookNowData;
        if (formData) {
          setInitialValues((prev) => ({
            ...prev,
            message: decompress(ensureString(formData.ownerMessage)),
          }));
        }
      } else {
      // set departure time and message from session data for inquiry.
        const inquiryData = parsedData as InquiryData;
        if (inquiryData) {
          setInitialValues((prev) => ({
            ...prev,
            message: decompress(ensureString(inquiryData.comment)),
          }));
        }
      }
    } catch (error) {
      captureException(error);
    }
  }, [router.query.sessionKey]);

  // Panel type: drawer or aside

  const { l: isLargeScreen } = useMediaQueries();
  const panelType: 'drawer' | 'aside' = isLargeScreen ? 'aside' : 'drawer';
  // The panel that should be default
  // For aside, the root panel is always visible, this is the first modal panel
  const rootPanel = panelType === 'drawer' ? TRIP_CART_PANEL_ROOT : TRIP_CART_PANEL_CALENDAR;

  const showPanel = useCallback((panel: TripCartPanels, autoOpen: boolean = true) => {
    // Ensure no duplicates
    const previous = previousPanels.filter((v) => v !== panel);
    setActivePanel(panel);
    if (!isOpen && autoOpen) {
      setOpenInternal(true);
      // Will show immediately instead of animating in from right
      setPanelDirection('out');
      // Clicking done will auto-close it
      setPreviousPanels([]);
    } else {
      setPreviousPanels([...previous, activePanel]);
      setPanelDirection(panel === rootPanel ? 'out' : 'in');
    }
  }, [activePanel, isOpen, previousPanels, rootPanel]);

  const closePanel = useCallback((closeAll: boolean = false) => {
    setPanelDirection('out');
    const previous = [...previousPanels];
    if (previousPanels.length > 0) {
      const last = closeAll || !previous.length
        ? rootPanel
        : previous.pop();
      setActivePanel(last ?? rootPanel);
      setPreviousPanels(closeAll ? [] : previous);
    }
    if (panelType === 'aside' && previous.length === 0) {
      setOpenInternal(false);
    }

    if (panelType === 'drawer' && previous.length === 0) {
      setActivePanel(rootPanel);
    }
  }, [panelType, previousPanels, rootPanel]);

  const [snap, setSnap] = useState(MIN_SNAP_POINT);

  /** Open drawer or side panel */
  const setPanelOpen = useCallback((value: boolean) => {
    setOpenInternal(value);
    // reset drawer
    setPreviousPanels([]);
    setActivePanel(rootPanel);
    setPanelDirection('out');
  }, [rootPanel]);

  const [previousPanelType, setPreviousPanelType] = useState<PanelType>();

  const proceedWithSetup = useCallback(() => {
    if (Object.keys(initialValues).length) {
      setMotivatorsDates(initialValues.formSelectedDates);
      setStatus('ready');
    }
  }, [initialValues, setMotivatorsDates]);

  // Reset drawer or panel
  useEffect(() => {
    if (previousPanelType !== panelType) {
      setPreviousPanelType(panelType);
      setPanelOpen(false);
    }
  }, [panelType, previousPanelType, setPanelOpen]);

  useEffect(
    () => {
      router.events.on('routeChangeStart', clearCaches);
      return () => router.events.off('routeChangeStart', clearCaches);
    },
    [router],
  );
  // Load sequencing
  useEffect(() => {
    // prevent SSR calls
    if (!isMounted) {
      // May be called multiple times, this is ok
      clearCaches();
      return;
    }

    if (status === 'initializing') {
      proceedWithSetup();
    }
  }, [isMounted, initialValues, status, setMotivatorsDates, proceedWithSetup]);

  useEffect(() => {
    const hasSheetParam = Object.hasOwn(router.query, 'sheet');
    const hasSessionKeyParam = Object.hasOwn(router.query, 'sessionKey');
    if (hasSheetParam) {
      setPanelOpen(true);
      setMotivatorsDates(initialValues.formSelectedDates);
    }
    if (hasSessionKeyParam) {
      restoreMessage();
      sessionStore.removeItem(ensureString(router.query.sessionKey));
    }

    if (Object.hasOwn(router.query, 'error')) {
      broadcastNotification({
        i18nKey: 'inquiry-success--broadcast-boats-error-message',
        translation: 'inquiry-success',
        variant: 'error',
      });
    }

    if (Object.hasOwn(router.query, PARAM_CHECKOUT_INSTABOOK_GUEST_ERROR)) {
      broadcastNotification({
        i18nKey: 'trip-cart--form-error--checkout-instabook-guest',
        translation: 'listing-page',
        variant: 'error',
      });
    }
    removeSearchParams(router.query, ['error', 'sheet', 'sessionKey', 'session_id', PARAM_CHECKOUT_INSTABOOK_GUEST_ERROR], router.replace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo((): TripCartContextProps => ({
    initialValues,
    status,
    isOpen,
    listing,
    activePanel,
    showPanel,
    closePanel,
    isLargeScreen,
    panelType,
    threadId,
    snap,
    setSnap,
    snoozeActive: !!snoozeActive,
    addonsOptional,
    addonsLater,
    panelDirection,
    setOpen: setPanelOpen,
    showAiSummary,
    hasDepartureTimes,
    setHasDepartureTimes,
  }), [
    initialValues,
    isOpen,
    listing,
    status,
    activePanel,
    showPanel,
    closePanel,
    isLargeScreen,
    panelType,
    threadId,
    snap,
    setSnap,
    snoozeActive,
    addonsOptional,
    addonsLater,
    panelDirection,
    setPanelOpen,
    showAiSummary,
    hasDepartureTimes,
    setHasDepartureTimes,
  ]);

  return (
    <TripCartContext.Provider value={value}>
      {children}
    </TripCartContext.Provider>
  );
};

export default TripCartProvider;

export const useTripCartContext = (): TripCartContextProps => {
  const context = useContext(TripCartContext);
  if (context == null) {
    throw new Error('useTripCartContext must be used within a TripCartProvider');
  }
  return context;
};
